import React, { useContext, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import {
  getAskingPrice,
  mapValueToEbitdaFormat,
  mapValueToMoneyFormat,
} from 'utils';
import {
  ASKING_PRICE_SELECT,
  AskingPriceSelect,
  Currency,
  STOCK_AT_VALUE,
  StockAtValue,
} from 'models/DealRoom';
import { RequiredVisibleInfo } from 'models/Listing';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import ResolvePopup from 'components/ResolvePopup';
import InformationcircleIcon from 'icons/Informationcircle.icon';
import useShowFieldVisibleInfo from 'hooks/useShowFieldVisibleInfo';

import './ListingPrices.styles.scss';
import './ListingPrices.styles.responsive.scss';

type ListingPricesProps = {
  className?: string;
  askingPrice: AskingPriceSelect;
  amount: number;
  amountFrom: number;
  amountTo: number;
  revenue: number;
  EBITDA: number;
  averageStockValue: number;
  stockAtValue: StockAtValue;
  currency: Currency;
  amountRequiredVisibleInfo?: RequiredVisibleInfo;
  amountFromRequiredVisibleInfo?: RequiredVisibleInfo;
  amountToRequiredVisibleInfo?: RequiredVisibleInfo;
  revenueRequiredVisibleInfo?: RequiredVisibleInfo;
  EBITDARequiredVisibleInfo?: RequiredVisibleInfo;
  averageStockValueRequiredVisibleInfo?: RequiredVisibleInfo;
};

const ListingPrices: React.FC<ListingPricesProps> = (props) => {
  const {
    className,
    amount,
    amountTo,
    amountFrom,
    askingPrice,
    averageStockValue,
    EBITDA,
    revenue,
    stockAtValue,
    currency,
    amountRequiredVisibleInfo,
    amountFromRequiredVisibleInfo,
    amountToRequiredVisibleInfo,
    revenueRequiredVisibleInfo,
    EBITDARequiredVisibleInfo,
    averageStockValueRequiredVisibleInfo,
  } = props;

  const { currentUser } = useContext(CurrentUserContext);

  const { t } = useTranslation();

  const getShowFieldVisibleInfo = useShowFieldVisibleInfo();

  const showAskingPrice = useMemo(
    () =>
      getAskingPrice({
        amount,
        amountFrom,
        amountTo,
        askingPriceSelect: askingPrice,
      }),
    [amount, amountFrom, amountTo, askingPrice],
  );

  const askingPriceVisibleInfo = useMemo(() => {
    if (askingPrice === ASKING_PRICE_SELECT.FIXED_PRICE)
      return amountRequiredVisibleInfo;

    if (askingPrice === ASKING_PRICE_SELECT.PRICE_RANGE)
      return amountFromRequiredVisibleInfo || amountToRequiredVisibleInfo;

    return undefined;
  }, [
    askingPrice,
    amountRequiredVisibleInfo,
    amountFromRequiredVisibleInfo,
    amountToRequiredVisibleInfo,
  ]);

  const {
    isHidden: isAskingPriceHidden,
    reasonToHideContent: askingPriceReason,
  } = getShowFieldVisibleInfo(askingPriceVisibleInfo);

  const { isHidden: isRevenueHidden, reasonToHideContent: revenueReason } =
    getShowFieldVisibleInfo(revenueRequiredVisibleInfo);

  const { isHidden: isEBITDAHidden, reasonToHideContent: EBITDAReason } =
    getShowFieldVisibleInfo(EBITDARequiredVisibleInfo);

  const {
    isHidden: isAverageStockValueHidden,
    reasonToHideContent: averageStockValueReason,
  } = getShowFieldVisibleInfo(averageStockValueRequiredVisibleInfo);

  const hasEBITDAValue = !!EBITDA && EBITDA > 0;
  const hasStockValue =
    !!averageStockValue && stockAtValue !== STOCK_AT_VALUE.NO_STOCK_AT_VALUE;

  const showAskingPriceRow =
    !!askingPrice &&
    !!currentUser &&
    (!!showAskingPrice || isAskingPriceHidden);
  const showRevenueRow = !!revenue || isRevenueHidden;
  const showEBITDARow = hasEBITDAValue || isEBITDAHidden;
  const showAverageStockValueRow =
    stockAtValue !== STOCK_AT_VALUE.NO_STOCK_AT_VALUE &&
    (hasStockValue || isAverageStockValueHidden);

  const classes = classNames('listing-prices', className);

  const infoPopup = useMemo(
    () => (
      <ResolvePopup
        onHover
        popupContent={<span>{t('pricePopupContent')}</span>}
      >
        <InformationcircleIcon />
      </ResolvePopup>
    ),
    [t],
  );

  return (
    <div className={classes}>
      {showAskingPriceRow && (
        <div className="listing-prices__price">
          <span>{showAskingPrice || askingPriceReason}</span>
          <span>
            {currency
              ? t('priceGuideWithCurrency', { currency: t(currency) })
              : t('priceGuide')}
            {!showRevenueRow &&
              !showEBITDARow &&
              !showAverageStockValueRow &&
              infoPopup}
          </span>
        </div>
      )}
      {showRevenueRow && (
        <div className="listing-prices__price">
          <span>
            {revenue ? mapValueToMoneyFormat(revenue) : revenueReason}
          </span>
          <span>
            {t('revenue')}
            {!showEBITDARow && !showAverageStockValueRow && infoPopup}
          </span>
        </div>
      )}
      {showEBITDARow && (
        <div className="listing-prices__price">
          <span>
            {hasEBITDAValue ? mapValueToEbitdaFormat(EBITDA) : EBITDAReason}
          </span>
          <span>
            {t('ebitda')}
            {!showAverageStockValueRow && infoPopup}
          </span>
        </div>
      )}
      {showAverageStockValueRow && (
        <div className="listing-prices__price">
          <span>
            {hasStockValue
              ? mapValueToMoneyFormat(averageStockValue)
              : averageStockValueReason}
          </span>
          <span>
            {t(
              `stockAtValuation${
                stockAtValue === STOCK_AT_VALUE.INCLUDE_STOCK_AT_VALUE
                  ? 'Included'
                  : 'Excluded'
              }`,
            )}
            {infoPopup}
          </span>
        </div>
      )}
    </div>
  );
};

export default ListingPrices;
